import { inject, Injectable, signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Core as Cytoscape } from 'cytoscape';
import { Connection, jsPlumb, type jsPlumbInstance } from 'jsplumb';

import { SynopticStandardUtils } from '@connect-services/client/components/synoptic/synoptic-graph/synoptic-standard-color.utils';
import {
    cableRegex,
    ColorEnum,
    lightColors,
} from '@connect-services/client/components/synoptic/synoptic-plan/plan-core/synoptic-plan.constants';
import {
    Cable,
    Cassette,
    PositionEnum,
    SelectedNode,
    SynopticPlanItem,
} from '@connect-services/client/components/synoptic/synoptic-plan/plan-core/synoptic-plan.models';
import { AssertService } from '@connect-services/client/services/assert.service';
import { HasPermissionsService } from '@connect-services/client/services/has-permissions.service';
import { NetworkPathService } from '@connect-services/client/services/network-path/network-path.service';
import { OpticalPathExportService, SpecificFiber } from '@connect-services/client/services/optical-path-export.service';
import { SelectSynopticService } from '@connect-services/client/services/synoptic/select-synoptic.service';
import { SynopticPlanBuilderService } from '@connect-services/client/services/synoptic/synoptic-plan-builder.service';
import { SynopticPlanLayoutService } from '@connect-services/client/services/synoptic/synoptic-plan-layout.service';
import { CableDirectionEnum, SynopticBoxPlanItem } from '@connect-services/src/sdk/api';

interface CanvasConnection extends Connection {
    canvas: HTMLElement;
    bind(
        event: 'mouseover' | 'mouseout' | 'click' | 'contextmenu',
        handler: (connection: Connection, event: MouseEvent) => void,
    ): void;
}

@Injectable({
    providedIn: 'root',
})
export class SynopticPlanRendererService {
    public jsPlumbInstances: Record<string, jsPlumbInstance> = {};
    public readonly openedCassettes = signal<Cassette[]>([]);

    protected readonly selectSynopticService = inject(SelectSynopticService);

    private contextMenu: HTMLElement | null = null;
    private readonly fiberFocusedClass = 'jtk-connector-focused';
    private readonly fiberInformation = signal<SpecificFiber | undefined>(undefined);
    private readonly hasPermissionsService = inject(HasPermissionsService);
    private readonly networkPathService = inject(NetworkPathService);
    private readonly opticalPathExportService = inject(OpticalPathExportService);
    private readonly synopticPlanBuilderService = inject(SynopticPlanBuilderService);
    private readonly synopticPlanLayoutService = inject(SynopticPlanLayoutService);
    private readonly translocoService = inject(TranslocoService);

    public generateCableAndTubes(
        cassetteKey: string,
        cableData: Record<string, Record<string, Cable>>,
        cy: Cytoscape,
        onUpdateNode: (node: SelectedNode) => void,
        selectedNode: SelectedNode,
    ): { left?: HTMLElement; right?: HTMLElement } {
        const styleSheet = (document.querySelector('#styleSheetEl') as HTMLStyleElement).sheet as CSSStyleSheet;
        const htmlCables: { left?: HTMLElement; right?: HTMLElement } = {};

        Object.keys(cableData).forEach((side) => {
            const direction = side === 'entrant' ? CableDirectionEnum.LEFT : CableDirectionEnum.RIGHT;

            const divContainer = document.createElement('div');
            divContainer.setAttribute('class', 'boxPlan__side');
            htmlCables[direction] = divContainer;

            const cables = cableData[side];

            Object.keys(cables).forEach((cableKey) => {
                const cable = cables[cableKey];

                // Avoid to build cable with no values
                if (Object.keys(cables).length > 1 && cableKey === 'null') {
                    return;
                }

                const cableTubeGroup = document.createElement('div');
                cableTubeGroup.setAttribute(
                    'class',
                    `boxPlan__cableTubeGroup  boxPlan__cableTubeGroup__${cableKey} boxPlan__cableTubeGroup__${direction}`,
                );
                const tubesKeys: number[] = [];
                Object.keys(cable.tubes).forEach((value) => tubesKeys.push(parseInt(value)));
                cableTubeGroup.setAttribute('data-minTubeValue', `${Math.min(...tubesKeys)}`);

                cableTubeGroup.addEventListener('click', () => {
                    const indexRule2 = Object.values(styleSheet.cssRules).find((rule) =>
                        cableRegex.test((rule as CSSStyleRule).selectorText),
                    ) as CSSStyleRule;

                    let id = undefined;
                    if (indexRule2) {
                        const parts = indexRule2.selectorText.split('__');
                        const lastIndex = parts.length - 1;
                        id = parts[lastIndex];
                    }

                    if (id && id === cableKey) {
                        this.removeCableFocus(styleSheet);

                        return;
                    } else if (id && id !== cableKey) {
                        this.removeCableFocus(styleSheet);
                    }

                    styleSheet.insertRule(`.boxPlan__cableTubeGroup__${cableKey} {opacity: 1 !important;}`);
                    styleSheet.insertRule('.boxPlan__cableTubeGroup {opacity: 0.3;');
                });

                const divCables = document.createElement('div');
                divCables.setAttribute('class', `boxPlan__cables boxPlan__cables__${direction}`);

                const divCable = document.createElement('div');
                divCable.setAttribute(
                    'class',
                    `boxPlan__cable boxPlan__cable__${cableKey} boxPlan__cable__${direction}`,
                );

                const mainParagraph = document.createElement('p');
                mainParagraph.textContent = this.translocoService.translate('synopticPlan.cable');
                const etiquetSpan = document.createElement('span');
                etiquetSpan.textContent = cable.etiquet ?? '';

                const button = document.createElement('button');

                const nextRawNode = cy.$id(cable.boiteBaie as string);
                if (nextRawNode.length > 0) {
                    if (direction === CableDirectionEnum.LEFT) {
                        button.textContent = this.translocoService.translate('synopticPlan.upstreamCable');
                    } else {
                        button.textContent = this.translocoService.translate('synopticPlan.downstreamCable');
                    }

                    button.addEventListener('click', (evt) => {
                        evt.stopPropagation();
                        const node = this.synopticPlanLayoutService.prepareNode(nextRawNode);

                        onUpdateNode(node);
                    });
                } else if (selectedNode?.dataNode.nbPto) {
                    button.textContent = 'PTO / DTIO';
                    button.disabled = true;
                } else {
                    if (direction === CableDirectionEnum.LEFT) {
                        button.textContent = this.translocoService.translate('synopticPlan.upstreamCable');
                    } else {
                        button.textContent = this.translocoService.translate('synopticPlan.downstreamCable');
                    }
                    button.disabled = true;
                }

                let arrowIcon;
                if (nextRawNode.length > 0) {
                    arrowIcon = document.createElement('i');
                    arrowIcon.setAttribute('class', `icofont-arrow-${direction}`);
                    if (direction === CableDirectionEnum.LEFT) {
                        button.prepend(arrowIcon);
                    } else {
                        button.appendChild(arrowIcon);
                    }
                }

                mainParagraph.appendChild(etiquetSpan);

                divCable.appendChild(mainParagraph);
                divCable.appendChild(this.generateCodextOrEtiquetElement(cableKey, 'synopticPlan.code', '0.5rem'));

                if (cable.codeext) {
                    divCable.appendChild(
                        this.generateCodextOrEtiquetElement(cable.codeext, 'synopticPlan.externalCode'),
                    );
                }

                if (cable.boiteBaie) {
                    const pBox = document.createElement('p');
                    if (direction === 'left') {
                        pBox.textContent = this.translocoService.translate('synopticPlan.upstreamBoxBay');
                    } else {
                        pBox.textContent = this.translocoService.translate('synopticPlan.downstreamBoxBay');
                    }
                    const spanBox = document.createElement('span');
                    spanBox.textContent = cable.boiteBaieEtiquet;

                    if (cable.codeext) {
                        pBox.style.paddingTop = '0.5rem';
                    }

                    pBox.appendChild(spanBox);
                    divCable.appendChild(pBox);
                }

                if (cable.boiteBaie) {
                    divCable.appendChild(this.generateCodextOrEtiquetElement(cable.boiteBaie, 'synopticPlan.code'));
                }

                if (cable.boiteBaieCodeext) {
                    divCable.appendChild(
                        this.generateCodextOrEtiquetElement(cable.boiteBaieCodeext, 'synopticPlan.externalCode'),
                    );
                }

                divCable.appendChild(button);

                divCables.appendChild(divCable);
                cableTubeGroup.appendChild(divCables);

                // Build tubes for this cable
                const tubes = cable.tubes;

                const tubesContainer = document.createElement('div');
                tubesContainer.setAttribute('class', `boxPlan__tubes boxPlan__tubes__${direction}`);
                Object.keys(tubes).forEach((tubeKey) => {
                    const tube = tubes[tubeKey];

                    tube.color = SynopticStandardUtils.formatColor(Number(tubeKey));

                    const div = document.createElement('div');
                    div.setAttribute('id', `boxPlan_tube_${cassetteKey}_${cableKey}_${tubeKey}`);
                    div.setAttribute('class', `boxPlan__tube boxPlan__tube__${direction}`);
                    const mainParagraph = document.createElement('p');
                    const p2 = document.createElement('p');
                    const tubeColor = document.createElement('span');
                    const p3 = document.createElement('p');
                    const fiberType = document.createElement('span');

                    mainParagraph.textContent = `${this.translocoService.translate('synopticPlan.tube')} ${tubeKey}`;
                    p2.textContent = tube.color;
                    p3.textContent = tube.typeFibre;
                    tubeColor.textContent = this.translocoService.translate('synopticPlan.tubeColor');
                    fiberType.textContent = this.translocoService.translate('synopticPlan.fiberType');

                    tubeColor.classList.add('tube-description');
                    fiberType.classList.add('tube-description');

                    p2.prepend(tubeColor);
                    p3.prepend(fiberType);
                    div.appendChild(mainParagraph);
                    div.appendChild(p2);
                    div.appendChild(p3);

                    // Adapt styles for better accessibility
                    if (tube.color && lightColors.includes(tube.color)) {
                        div.style.color = '#1f2f3c';
                        tubeColor.style.color = '#1f2f3c';
                        fiberType.style.color = '#1f2f3c';

                        if (tube.color === ColorEnum.WHITE) {
                            if (direction === CableDirectionEnum.LEFT) {
                                div.style.borderLeft = 'none';
                            } else {
                                div.style.borderRight = 'none';
                            }
                        }
                    }

                    if (tube.color) {
                        div.style.background = tube.color;
                    } else {
                        div.style.backgroundImage =
                            'linear-gradient(130deg, #0099fa 25%, #000000 25%, #000000 50%, #0099fa 50%, #0099fa 75%, #000000 75%, #000000 100%)';
                        div.style.color = 'white';
                        tubeColor.style.color = 'white';
                        fiberType.style.color = 'white';
                    }

                    tubesContainer.appendChild(div);
                });

                if (Object.keys(cables).length <= 1 && cableKey === 'null') {
                    cableTubeGroup.style.visibility = 'hidden';
                }

                cableTubeGroup.appendChild(tubesContainer);

                htmlCables[direction]?.appendChild(cableTubeGroup);
            });
        });

        return htmlCables;
    }

    public generateCassette(key: string, cassette: SynopticBoxPlanItem[]): HTMLDivElement {
        // Sort each cassette in cassettes
        const decomposedCassette = this.decomposeCassette(cassette);

        // Build header
        const header = document.createElement('div');
        header.setAttribute('class', 'boxPlan__cassette__header');

        // build Title
        const title = document.createElement('div');

        const name = document.createElement('p');
        name.textContent = this.translocoService.translate('synopticPlan.cassette');
        title.appendChild(name);

        const cassetteNum = document.createElement('p');
        cassetteNum.textContent = `${Object.values(cassette)[0].numero_cassette} : `;
        title.appendChild(cassetteNum);

        const cassetteValue = document.createElement('p');
        cassetteValue.textContent = key;
        title.appendChild(cassetteValue);
        header.appendChild(title);

        //Build icon
        const icon = document.createElement('i');
        icon.setAttribute('class', 'icofont-simple-down');
        icon.addEventListener('click', (evt) => {
            const icon = evt.target as HTMLElement;
            const headerElement: HTMLElement = icon.parentElement!.parentElement!;
            this.switchCassetteState(icon, headerElement, key, decomposedCassette);
        });
        header.appendChild(icon);

        const spinner = document.createElement('i');
        spinner.setAttribute('class', 'fas fa-circle-notch rotating');
        header.appendChild(spinner);

        // Build cassette container
        const cassetteContainer = document.createElement('div');
        cassetteContainer.setAttribute('id', `plan_cassette_${key}`);
        cassetteContainer.setAttribute('class', 'boxPlan__cassette');
        cassetteContainer.appendChild(header);

        // Build content
        const cassetteContent = document.createElement('div');
        cassetteContent.setAttribute('id', 'boxPlan_container_content');
        cassetteContent.setAttribute('class', 'boxPlan__cassette__content');

        // Build header row
        const headerRow = document.createElement('div');
        const incomingPosition = document.createElement('p');

        const connectDiv = document.createElement('div');
        const internConnectDiv = document.createElement('div');
        const connectorizationEl = document.createElement('p');
        const spliceEl = document.createElement('p');
        const exchangeIcon = document.createElement('i');

        const outgoingPosition = document.createElement('p');
        headerRow.setAttribute('class', 'boxPlan__cassette__content__row');
        incomingPosition.textContent = this.translocoService.translate('synopticPlan.incomingPosition');
        connectorizationEl.textContent = this.translocoService.translate('synopticPlan.connectorization');
        spliceEl.textContent = this.translocoService.translate('synopticPlan.splice');
        spliceEl.setAttribute('class', 'boxPlan__cassette__content__inactiveTitle');
        exchangeIcon.setAttribute('class', 'icofont-exchange');
        outgoingPosition.textContent = this.translocoService.translate('synopticPlan.outgoingPosition');
        internConnectDiv.appendChild(connectorizationEl);
        internConnectDiv.appendChild(spliceEl);
        connectDiv.appendChild(internConnectDiv);
        connectDiv.appendChild(exchangeIcon);
        connectDiv.addEventListener('click', (evt) => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            let target = evt.target.parentNode;

            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            if (evt.target.tagName === 'P') {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-expect-error
                target = evt.target.parentNode.parentNode;
            }

            if (target.classList.contains('boxPlan__cassette__content__row')) {
                target = evt.target;
            }

            const firstChild = target.firstChild.children[0];
            const secondChild = target.firstChild.children[1];

            firstChild.classList.toggle('boxPlan__cassette__content__inactiveTitle');
            secondChild.classList.toggle('boxPlan__cassette__content__inactiveTitle');
            target.firstChild.classList.toggle('boxPlan__cassette__content__reverseTitle');

            const contentRows = target.parentNode.parentNode.children;
            const dataKey = firstChild.classList.contains('boxPlan__cassette__content__inactiveTitle')
                ? 'splice'
                : 'connectorization';

            // i begin at 1 to avoid the header row
            for (let i = 1; i < contentRows.length; i++) {
                const element = contentRows[i];
                const span = element.children[1].firstChild;
                span.textContent = span.dataset[dataKey];
            }
        });

        headerRow.appendChild(incomingPosition);
        headerRow.appendChild(connectDiv);
        headerRow.appendChild(outgoingPosition);
        cassetteContent.appendChild(headerRow);

        // Build fibres
        decomposedCassette.forEach((fibre, fibreKey) => {
            const splice = fibre.type_epissurage ?? '';
            const fibreStyle = this.getFibreStyle(fibre);
            const borderStyle = '4px solid';
            const boxShadow = `0 0 0 1px black`;

            const row = document.createElement('div');
            row.setAttribute('class', `boxPlan__cassette__content__row ${fibreStyle.class}`);

            const mainParagraph = document.createElement('p');
            mainParagraph.setAttribute(
                'id',
                `boxPlan_fibre_${fibre.cassette}_${fibre.cable_entrant}_${fibre.numero_tube_entrant}_${fibreKey}`,
            );
            const p2 = document.createElement('p');
            const p3 = document.createElement('p');
            p3.setAttribute(
                'id',
                `boxPlan_fibre_${fibre.cassette}_${fibre.cable_sortant}_${fibre.numero_tube_sortant}_${fibreKey}`,
            );
            const positionFiberEntr = document.createElement('span');
            const middle = document.createElement('span');
            const positionFibreSort = document.createElement('span');

            positionFiberEntr.textContent = `${
                fibre.numero_fibre_entrante
                    ? fibre.numero_fibre_entrante.toString().length === 1
                        ? `0${fibre.numero_fibre_entrante}`
                        : fibre.numero_fibre_entrante
                    : '00'
            }`;
            const connectorization = fibre.connectorisation || 'N/A';

            middle.textContent = `${fibre.numero_position}. ${connectorization}`.concat(
                ...(fibre.type_ingenierie ? [' - ', fibre.type_ingenierie] : []),
                ...(fibre.affectation ? ['-', fibre.affectation] : []),
            );
            middle.setAttribute('data-connectorization', connectorization);
            middle.setAttribute('data-splice', splice);

            positionFibreSort.textContent = `${
                fibre.numero_fibre_sortante
                    ? fibre.numero_fibre_sortante.toString().length === 1
                        ? `0${fibre.numero_fibre_sortante}`
                        : fibre.numero_fibre_sortante
                    : '00'
            }`;

            if (fibreStyle.class.includes('waitRight')) {
                row.setAttribute('style', `--borderBottom: ${borderStyle} ${fibreStyle.colors}`);
                positionFiberEntr.style.border = `${borderStyle} ${fibreStyle.colors}`;
                middle.style.border = `${borderStyle} ${fibreStyle.colors}`;
                positionFibreSort.style.border = `${borderStyle} transparent`;
            } else if (fibreStyle.class.includes('waitLeft')) {
                row.setAttribute('style', `--borderBottom: ${borderStyle} ${fibreStyle.colors}`);
                positionFiberEntr.style.border = `${borderStyle} transparent`;
                middle.style.border = `${borderStyle} ${fibreStyle.colors}`;
                positionFibreSort.style.border = `${borderStyle} ${fibreStyle.colors}`;
            } else {
                row.setAttribute(
                    'style',
                    `--borderBottom: ${borderStyle} ${fibreStyle.colors[0]}; --borderTop: ${borderStyle} ${fibreStyle.colors[1]};`,
                );
                positionFiberEntr.style.border = `${borderStyle} ${fibreStyle.colors[0]}`;
                middle.style.borderLeft = `${borderStyle} ${fibreStyle.colors[0]}`;
                middle.style.borderTop = `${borderStyle} ${fibreStyle.colors[0]}`;
                middle.style.borderRight = `${borderStyle} ${fibreStyle.colors[1]}`;
                middle.style.borderBottom = `${borderStyle} ${fibreStyle.colors[1]}`;
                positionFibreSort.style.border = `${borderStyle} ${fibreStyle.colors[1]}`;
            }

            positionFiberEntr.style.boxShadow = boxShadow;
            positionFibreSort.style.boxShadow = boxShadow;
            middle.style.boxShadow = boxShadow;

            mainParagraph.appendChild(positionFiberEntr);
            p2.appendChild(middle);
            p3.appendChild(positionFibreSort);
            row.appendChild(mainParagraph);
            row.appendChild(p2);
            row.appendChild(p3);
            cassetteContent.appendChild(row);
        });

        cassetteContainer.appendChild(cassetteContent);

        return cassetteContainer;
    }

    public hideAllCassettes(): void {
        const contentsOfCassettes = document.querySelectorAll('[id^="plan_cassette_"] .boxPlan__cassette__content');
        contentsOfCassettes.forEach((cassette) => {
            (cassette as HTMLElement).style.display = 'none';
        });
    }

    public initContextMenuOnFiberClick(selectedNode: SelectedNode, layerId: string): void {
        this.contextMenu = document.createElement('div');
        this.contextMenu.id = 'boxPlan_fibre_context_menu';
        this.contextMenu.className = 'custom-context-menu';
        this.contextMenu.style.position = 'absolute';
        this.contextMenu.style.display = 'none';

        document.body.appendChild(this.contextMenu);

        const exportMenuItem = document.createElement('div');
        exportMenuItem.className = 'custom-context-menu-item';
        exportMenuItem.dataset['action'] = 'export';

        exportMenuItem.onclick = (): void => {
            this.opticalPathExportService.exportOpticalPath(
                this.selectSynopticService.selectedDeposit()!.id,
                selectedNode.dataNode.uuid,
                this.opticalPathExportService.findBusinessIdentifier(selectedNode),
                this.fiberInformation(),
            );

            this.hideContextMenu();
        };

        if (this.networkPathService.canAccessNetworkPath()) {
            this.addNetworkPathMenuItem(layerId, selectedNode.dataNode.uuid);
        }

        this.contextMenu.appendChild(exportMenuItem);
    }

    public removeCableFocus(styleSheet: CSSStyleSheet): void {
        const rule1 = Object.values(styleSheet.rules).findIndex(
            (rule) => (rule as CSSStyleRule).selectorText === '.boxPlan__cableTubeGroup',
        );
        styleSheet.deleteRule(rule1);

        const rule2 = Object.values(styleSheet.rules).findIndex((rule) =>
            cableRegex.test((rule as CSSStyleRule).selectorText),
        );
        styleSheet.deleteRule(rule2);
    }

    public resetPlanHtml(): void {
        const containerChildren = document.querySelector('#boxPlan_container') as HTMLElement;
        if (!containerChildren) {
            return;
        }
        while (containerChildren.firstChild) {
            containerChildren.removeChild(containerChildren.lastChild as HTMLElement);
        }
    }

    public switchCassetteState(
        target: HTMLElement,
        parent: HTMLElement,
        key: string,
        decomposedCassette: SynopticBoxPlanItem[],
    ): void {
        const icons = parent.querySelectorAll<HTMLElement>('.boxPlan__cassette__header i');
        const container = parent.querySelector<HTMLElement>('#boxPlan_container_content') as HTMLElement;

        const jsPlumbInstances = this.jsPlumbInstances;
        let jsPlumbInstance;
        let jsPlumbIsUndefined;
        if (jsPlumbInstances[key] === undefined) {
            const view = document.querySelector(`#boxPlan_view_${key}`);
            jsPlumbInstances[key] = jsPlumb.getInstance({ Container: view });
            jsPlumbInstances[key].importDefaults({
                ConnectionsDetachable: false,
                LogEnabled: true, // Turn false
            });

            jsPlumbInstance = jsPlumbInstances[key];
            jsPlumbIsUndefined = true;
        } else {
            jsPlumbInstance = jsPlumbInstances[key];
            jsPlumbIsUndefined = false;
        }

        if (container.style.display === 'block') {
            container.style.display = 'none';
            target.classList.replace('icofont-simple-up', 'icofont-simple-down');
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            jsPlumbInstance.select().setVisible(false);
            this.toggleCables(key, 'none');
            this.toggleTubes(key, 'none', '0');

            this.openedCassettes.update((cassettes: Cassette[]) =>
                cassettes.filter((cassette: Cassette) => cassette.key !== key),
            );
        } else {
            icons[0].style.display = 'none';
            icons[1].style.display = 'block';
            setTimeout(() => {
                container.style.display = 'block';
                target.classList.replace('icofont-simple-down', 'icofont-simple-up');
                parent.scrollIntoView({ behavior: 'instant', block: 'start', inline: 'nearest' });
                this.toggleCables(key, 'flex');
                this.toggleTubes(key, 'flex', '8.5rem');

                if (jsPlumbIsUndefined) {
                    this.generateFibres(jsPlumbInstance, key, decomposedCassette);
                } else {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-expect-error
                    jsPlumbInstance.select().setVisible(true);
                }
                icons[1].style.display = 'none';
                icons[0].style.display = 'block';

                this.openedCassettes.update((cassettes) => [
                    ...cassettes,
                    new Cassette(this.decomposeCassette(decomposedCassette), key, parent, target),
                ]);
            }, 0);
        }
    }

    public toggleCables(viewKey: string, cssDisplayValue: string, allCables = false): void {
        const selector: string = allCables ? '.boxPlan__cable' : `#boxPlan_view_${viewKey} .boxPlan__cable`;
        const cables: NodeListOf<Element> = document.querySelectorAll(selector);

        cables.forEach((cableElement: Element): void => {
            const cable = cableElement as HTMLElement;

            (cable.firstChild as HTMLElement).style.paddingBottom = cssDisplayValue === 'none' ? 'auto' : '0.2rem';

            // Begin after the first element and stop before the last element
            for (let i: number = 1; i < cable.children.length - 1; i++) {
                const child = cable.children[i] as HTMLElement;
                child.style.display = cssDisplayValue;
            }
        });
    }

    public toggleTubes(viewKey: string, cssDisplayValue: string, cssMinHeight: string, allTubes = false): void {
        const selector: string = allTubes ? '.boxPlan__tube' : `#boxPlan_view_${viewKey} .boxPlan__tube`;
        const tubes: NodeListOf<Element> = document.querySelectorAll(selector);

        tubes.forEach((tubeElement: Element): void => {
            const tube = tubeElement as HTMLElement;
            tube.style.minHeight = cssMinHeight;

            for (let i: number = 1; i < tube.children.length; i++) {
                const child = tube.children[i] as HTMLElement;
                child.style.display = cssDisplayValue;
            }
        });
    }

    private addNetworkPathMenuItem(layerId: string, nodeId: string): void {
        const menuItem: HTMLDivElement = document.createElement('div');
        menuItem.className = 'custom-context-menu-item';
        menuItem.dataset['action'] = 'viewOpticalPath';

        menuItem.onclick = (): void => {
            this.networkPathService.networkPathRedirection(layerId, nodeId, this.fiberInformation()?.positionId);
            this.hideContextMenu();
        };

        this.contextMenu?.appendChild(menuItem);
    }

    /**
     * Split the cassette in 3 : ps 1 abs, ps2 abs, ps1 et ps2 connected
     * Then rebuild it
     * @param groupedCassette
     */
    private decomposeCassette(groupedCassette: SynopticBoxPlanItem[]): SynopticBoxPlanItem[] {
        const leftPosition: SynopticBoxPlanItem[] = [];
        const rightPosition: SynopticBoxPlanItem[] = [];
        const connected: SynopticBoxPlanItem[] = [];

        groupedCassette.forEach((cassette: SynopticBoxPlanItem): void => {
            const incomingCable = cassette.cable_entrant;
            const outgoingCable = cassette.cable_sortant;

            if (!incomingCable && !outgoingCable) {
                return;
            }

            if (incomingCable && outgoingCable) {
                connected.push(cassette);
            }

            if (!incomingCable && outgoingCable) {
                leftPosition.push(cassette);
            }

            if (incomingCable && !outgoingCable) {
                rightPosition.push(cassette);
            }
        });

        const sortedleftPosition: SynopticBoxPlanItem[] = leftPosition.toSorted(
            this.synopticPlanBuilderService.sortByPosition(PositionEnum.SORTANT),
        );

        const sortedrightPosition: SynopticBoxPlanItem[] = rightPosition.toSorted(
            this.synopticPlanBuilderService.sortByPosition(PositionEnum.ENTRANT),
        );

        const sortedConnected: SynopticBoxPlanItem[] = connected.toSorted(
            this.synopticPlanBuilderService.sortByPosition(PositionEnum.ENTRANT),
        );

        return [...sortedleftPosition, ...sortedrightPosition, ...sortedConnected];
    }

    private generateCodextOrEtiquetElement(
        value: string | null,
        translationKey: string,
        paddingBottom?: string,
    ): HTMLElement {
        const pEtiquet = document.createElement('p');
        pEtiquet.textContent = this.translocoService.translate(translationKey);
        if (paddingBottom) {
            pEtiquet.style.paddingBottom = paddingBottom;
        }

        const spanEtiquet = document.createElement('span');
        spanEtiquet.textContent = value;

        pEtiquet.appendChild(spanEtiquet);

        return pEtiquet;
    }

    private generateFibres(
        jsPlumbInstance: jsPlumbInstance,
        cassetteKey: string,
        cassetteValues: SynopticPlanItem[],
    ): void {
        cassetteValues.forEach((fibre: SynopticPlanItem, fibreKey: number): void => {
            const leftFibreCode = fibre.fibre_entrante;
            const leftSource = document.querySelector(
                `#boxPlan_tube_${cassetteKey}_${fibre.cable_entrant}_${fibre.numero_tube_entrant}`,
            );
            const leftTarget = document.querySelector(
                `#boxPlan_fibre_${cassetteKey}_${fibre.cable_entrant}_${fibre.numero_tube_entrant}_${fibreKey}`,
            );
            const leftColor = SynopticStandardUtils.formatColor(Number(fibre.numero_fibre_entrante));

            const leftPaintStyle = {
                outlineStroke: '#1f2f3c',
                outlineWidth: 1,
                stroke: leftColor,
                strokeWidth: 5,
                ...(fibre.est_en_continuite
                    ? { 'stroke-linecap': 'butt' }
                    : { 'stroke-dasharray': '20', 'stroke-linecap': 'square' }),
            };

            const rightFibreCode = fibre.fibre_sortante;
            const rightSource = document.querySelector(
                `#boxPlan_tube_${cassetteKey}_${fibre.cable_sortant}_${fibre.numero_tube_sortant}`,
            );
            const rightTarget = document.querySelector(
                `#boxPlan_fibre_${cassetteKey}_${fibre.cable_sortant}_${fibre.numero_tube_sortant}_${fibreKey}`,
            );
            const rightColor = SynopticStandardUtils.formatColor(Number(fibre.numero_fibre_sortante));

            const rightPaintStyle = {
                outlineStroke: '#1f2f3c',
                outlineWidth: 1,
                stroke: rightColor,
                strokeWidth: 5,
                ...(fibre.est_en_continuite
                    ? { 'stroke-linecap': 'butt' }
                    : { 'stroke-dasharray': '20', 'stroke-linecap': 'square' }),
            };

            if (leftSource && leftTarget) {
                const leftConnection = jsPlumbInstance.connect({
                    anchors: ['Right', 'Left'],
                    connector: ['Bezier', { curviness: 100 }],
                    endpoint: 'Blank',
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-expect-error
                    endpointStyle: { fill: leftColor },
                    paintStyle: leftPaintStyle, //Connector style
                    source: leftSource,
                    target: leftTarget,
                });

                AssertService.isString(leftFibreCode);

                this.setFibreLabel(leftConnection, leftFibreCode, leftColor, fibre, CableDirectionEnum.LEFT);
            }

            if (rightSource && rightTarget) {
                const rightConnection = jsPlumbInstance.connect({
                    anchors: ['Left', 'Right'],
                    connector: ['Bezier', { curviness: 100 }],
                    endpoint: 'Blank',
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-expect-error
                    endpointStyle: { fill: rightColor },
                    paintStyle: rightPaintStyle, //Connector style
                    source: rightSource,
                    target: rightTarget,
                });

                AssertService.isString(rightFibreCode);

                this.setFibreLabel(rightConnection, rightFibreCode, rightColor, fibre, CableDirectionEnum.RIGHT);
            }
        });
    }

    private getFibreStyle(fibre: SynopticBoxPlanItem): { class: string; colors: string | string[] } {
        if (!fibre.numero_fibre_entrante && !fibre.numero_fibre_sortante) {
            return { class: '', colors: 'transparent' };
        }

        if (!fibre.numero_fibre_sortante) {
            return {
                class: 'boxPlan__cassette__content__row__waitRight',
                colors: SynopticStandardUtils.formatColor(Number(fibre.numero_fibre_entrante)),
            };
        }

        if (!fibre.numero_fibre_entrante) {
            return {
                class: 'boxPlan__cassette__content__row__waitLeft',
                colors: SynopticStandardUtils.formatColor(Number(fibre.numero_fibre_sortante)),
            };
        }

        return {
            class: 'boxPlan__cassette__content__row__connect',
            colors: [
                SynopticStandardUtils.formatColor(Number(fibre.numero_fibre_entrante)),
                SynopticStandardUtils.formatColor(Number(fibre.numero_fibre_sortante)),
            ],
        };
    }

    private hideContextMenu(): void {
        this.fiberInformation.set(undefined);

        if (!this.contextMenu) {
            return;
        }

        this.contextMenu.classList.remove('opacity-1');
        this.contextMenu.style.display = 'none';
    }

    private openFiberContextMenu(event: MouseEvent): void {
        if (!this.contextMenu) {
            return;
        }

        const exportItem = this.contextMenu.querySelector<HTMLElement>('[data-action="export"]');

        const viewOpticalPathItem = this.contextMenu.querySelector<HTMLElement>('[data-action="viewOpticalPath"]');

        if (exportItem) {
            exportItem.textContent = this.translocoService.translate('exportOpticalRoute', { COUNT: 1 });
        }

        if (viewOpticalPathItem) {
            viewOpticalPathItem.textContent = this.translocoService.translate('viewOpticalPath');

            viewOpticalPathItem.style.display = this.selectSynopticService.isEditMode() ? 'none' : 'block';
        }

        this.contextMenu.style.top = `${event.clientY}px`;
        this.contextMenu.style.left = `${event.clientX}px`;
        this.contextMenu.style.display = 'block';
        this.contextMenu.classList.add('opacity-1');

        const closeContext = (): void => {
            this.hideContextMenu();
        };

        setTimeout(() => document.addEventListener('click', closeContext, { once: true }), 0);
    }

    private setFibreLabel(
        connection: Connection,
        label: string,
        fibreColor: string,
        fibre: SynopticPlanItem,
        position: string,
    ): void {
        const tooltip: HTMLElement | null = document.querySelector('#boxPlan_fibre_tooltip');
        const tooltipValue: HTMLElement | null = document.querySelector('#boxPlan_fibre_tooltip_value');
        const tooltipWarning: HTMLElement | null = document.querySelector('#boxPlan_fibre_tooltip_warning');
        const fiColor = fibreColor.toString().trim();
        const fontColor = lightColors.includes(fiColor as ColorEnum) ? '#1f2f3c' : '#FFF';

        if (!tooltipValue || !tooltipWarning || !tooltip) {
            return;
        }

        // Display fibre label on hover
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        connection.bind('mouseover', (fibre, evt) => {
            const container = document.querySelector('#boxPlan_container') as HTMLElement;
            const rectContainer = container.getBoundingClientRect();
            const cx = evt.clientX - rectContainer.left; //x position within the element.
            const cy = evt.clientY - rectContainer.top; //y position within the element.

            tooltipValue.textContent = label;
            tooltip.style.left = `${cx - 50}px`;
            tooltip.style.background = fibreColor;
            tooltip.style.color = fontColor;
            tooltip.style.display = 'flex';
            if (fibreColor === '#FFF') {
                tooltipWarning.style.display = 'flex';
                tooltip.style.borderStyle = 'dashed'; // When fibre has no color
                tooltip.style.top = `${cy + 30}px`;
            } else {
                tooltip.style.borderStyle = 'solid';
                tooltip.style.top = `${cy + 40}px`;
            }
        });

        const extendedConnection = connection as CanvasConnection;

        extendedConnection.bind('mouseout', () => {
            tooltip.style.display = 'none';
            tooltipWarning.style.display = 'none';
        });

        extendedConnection.bind('click', (connection: Connection) => {
            this.toggleFibreFocus(connection as CanvasConnection);
        });

        extendedConnection.bind('contextmenu', (connection: Connection, event: MouseEvent) => {
            event.preventDefault();

            this.toggleFibreFocus(connection as CanvasConnection);

            this.fiberInformation.set({
                cassette: fibre.numero_cassette!,
                fiberPosition: fibre.numero_fibre_entrante ?? fibre!.numero_fibre_sortante!,
                incomingFiber: position === CableDirectionEnum.LEFT ? fibre.fibre_entrante! : undefined,
                outgoingFiber: position === CableDirectionEnum.RIGHT ? fibre.fibre_sortante! : undefined,
                positionId: fibre.ps_uuid ?? undefined,
            });

            this.openFiberContextMenu(event);
        });
    }

    private toggleFibreFocus(fibre: CanvasConnection): void {
        const container = document.querySelector('#boxPlan_container') as HTMLElement;
        const styleSheet = (document.querySelector('#styleSheetEl') as HTMLStyleElement).sheet as CSSStyleSheet;
        const rules = Object.values(styleSheet.cssRules);
        const focusedFibre = container.querySelectorAll('.boxPlan__fibre__focused');

        const elements = [fibre.canvas.firstChild, fibre.canvas.lastChild];

        if (focusedFibre.length) {
            focusedFibre.forEach((el) => {
                if (!elements.includes(el)) {
                    el.classList.remove('boxPlan__fibre__focused');
                }
            });
            document.querySelector(`.${this.fiberFocusedClass}`)?.classList.remove(this.fiberFocusedClass);
            fibre.canvas.classList.add(this.fiberFocusedClass);
        } else {
            styleSheet.insertRule('.jtk-connector path {opacity: 0.2}');
            fibre.canvas.classList.add(this.fiberFocusedClass);
        }

        let removeRule = false;
        elements.forEach((el) => {
            const elClasses = (el as HTMLElement).classList;
            if (elClasses.contains('boxPlan__fibre__focused')) {
                elClasses.remove('boxPlan__fibre__focused');
                removeRule = true;
            } else {
                elClasses.add('boxPlan__fibre__focused');
            }
        });

        if (removeRule) {
            const searchedRule = rules.find((rule) => (rule as CSSStyleRule).selectorText === '.jtk-connector path');
            if (searchedRule) {
                styleSheet.deleteRule(rules.indexOf(searchedRule));
            }
            document.querySelector(`.${this.fiberFocusedClass}`)?.classList.remove(this.fiberFocusedClass);
        }
    }
}
